import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router-dom";
import Button from "../../../shared/ui/Button";
import ConnectPlatformModal from "../components/ConnectPlatformModal";
import {
  PlatformsConnectionSkeleton,
  PlatformsSidebarSkeleton,
  PlatformsSyncLoadingPanel,
} from "../components/platforms/PlatformsLoadingState";
import {
  PLATFORM_IDS,
  GoogleBusinessLogo,
  JamedaLogo,
  KununuLogo,
} from "../platformCatalog";
import JamedaConnectModal from "../components/JamedaConnectModal";
import { disconnectJameda, fetchJamedaStatus } from "../jamedaApi";
import { hydrateConnectionsSnapshot } from "../platformConnectionsStorage";
import {
  PlanLimitBanner,
  PlanLimitDialog,
  PlanLimitSidebarCard,
} from "../components/platforms/PlanLimitNotice";
import {
  getPlatformById,
  googleStepCopy,
  formatRelativeTime,
  formatAvgRating,
  useTranslation,
} from "../../../i18n";
import GoogleLocationPickerModal from "../components/platforms/GoogleLocationPickerModal";
import {
  completeGoogleOAuth,
  connectGoogleBusiness,
  connectionFromBackendLocation,
  disconnectGoogleBusiness,
  fetchAvailableGoogleLocations,
  fetchGoogleIntegrationStatus,
  requireClientCompanyContext,
  resolveConnectionsFromIntegration,
} from "../googleBusinessApi";
import {
  getCurrentUser,
  saveOAuthSessionCheckpoint,
  clearOAuthSessionCheckpoint,
} from "../../../lib/authSession.js";

/** Per-company keys so another login on the same browser does not see the previous account's platforms. */
const connectionsStorageKey = (companyId) =>
  companyId ? `repman-client-platform-connections-v1:c-${companyId}` : null;

const googleIntegrationStorageKey = (companyId) =>
  companyId ? `repman-google-integration-id:c-${companyId}` : null;

const OAUTH_QUERY_KEYS = ["code", "state", "iss", "scope", "authuser", "prompt"];

function readHasStoredGoogleIntegration(companyId) {
  if (typeof window === "undefined") return false;
  const integrationKey = googleIntegrationStorageKey(companyId);
  return Boolean(integrationKey && localStorage.getItem(integrationKey));
}

/** True on first paint when returning from Google OAuth (avoids empty flash before effects run). */
function readGoogleOAuthReturnFromUrl() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("code") && params.get("state")) return true;

  const iss = params.get("iss") || "";
  const scope = params.get("scope") || "";
  return iss.includes("accounts.google.com") && scope.includes("business.manage");
}

function clearOAuthQueryParams(setSearchParams, currentParams) {
  const next = new URLSearchParams(currentParams);
  let changed = false;
  OAUTH_QUERY_KEYS.forEach((key) => {
    if (next.has(key)) {
      next.delete(key);
      changed = true;
    }
  });
  if (changed) {
    setSearchParams(next, { replace: true });
  }
}

const LinkChainIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EcosystemIllustration = () => (
  <svg
    width="176"
    height="176"
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-[#0d4d4d]"
    aria-hidden
  >
    <path
      d="M100 44 L148 78 L132 138 L68 138 L52 78 Z"
      stroke="#9dd5d5"
      strokeWidth="1.2"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M100 44 L100 92 M52 78 L100 92 M148 78 L100 92 M68 138 L100 92 M132 138 L100 92"
      stroke="#9dd5d5"
      strokeWidth="1.2"
    />
    <path d="M100 92 L100 118" stroke="#9dd5d5" strokeWidth="1.2" />
    <circle cx="100" cy="38" r="10" fill="#c5eae8" />
    <circle cx="156" cy="78" r="9" fill="#c5eae8" />
    <circle cx="134" cy="148" r="9" fill="#c5eae8" />
    <circle cx="66" cy="148" r="9" fill="#c5eae8" />
    <circle cx="44" cy="78" r="9" fill="#c5eae8" />
    <circle cx="100" cy="92" r="8" fill="#b3e0de" />
    <circle cx="100" cy="124" r="7" fill="#c5eae8" />
    <g transform="translate(72, 56)">
      <path
        d="M14 8a6 6 0 016-6 6 6 0 016 6v2M8 14a6 6 0 006 6 6 6 0 006-6v-2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M25 4v4h4M7 24v-4H3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <g transform="translate(118, 100)">
      <path
        d="M6 18h20a6 6 0 004.12-9.8 8 8 0 00-15.62-1.45A5.5 5.50 006 18z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M16 12v10M16 8l4 4M16 8l-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <circle cx="88" cy="128" r="4" fill="currentColor" />
  </svg>
);

function newConnectionKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `conn-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeStoredConnections(raw) {
  if (!raw) return [];
  try {
    const data = JSON.parse(raw);
    if (Array.isArray(data)) {
      return data
        .filter(
          (c) =>
            c &&
            PLATFORM_IDS.includes(c.platformId) &&
            typeof c.connectedAt === "number" &&
            Number.isFinite(c.connectedAt)
        )
        .map((c) => {
          const o = {
            connectionKey:
              typeof c.connectionKey === "string" && c.connectionKey.length > 0
                ? c.connectionKey
                : newConnectionKey(),
            platformId: c.platformId,
            connectedAt: c.connectedAt,
          };
          if (typeof c.displayName === "string" && c.displayName.trim()) {
            o.displayName = c.displayName.trim();
          }
          if (
            typeof c.avgRating === "number" &&
            Number.isFinite(c.avgRating)
          ) {
            o.avgRating =
              Math.round(Math.min(5, Math.max(1, c.avgRating)) * 10) / 10;
          }
          if (c.platformId === "google" && c.integrationId != null) {
            const iid = Number(c.integrationId);
            if (Number.isFinite(iid) && iid > 0) {
              o.integrationId = iid;
            }
          }
          if (c.locationId != null) {
            const lid = Number(c.locationId);
            if (Number.isFinite(lid) && lid > 0) o.locationId = lid;
          }
          if (typeof c.remoteId === "string" && c.remoteId.trim()) {
            o.remoteId = c.remoteId.trim();
          }
          return o;
        });
    }
    if (typeof data === "object" && data !== null) {
      const migrated = [];
      for (const id of PLATFORM_IDS) {
        if (typeof data[id] === "number" && Number.isFinite(data[id])) {
          migrated.push({
            connectionKey: `${id}-migrated-${data[id]}`,
            platformId: id,
            connectedAt: data[id],
          });
        }
      }
      return migrated;
    }
  } catch {
    /* ignore */
  }
  return [];
}

const ADD_ANOTHER_LABEL_KEY = {
  google: "platforms.addAnotherGoogle",
  jameda: "platforms.addAnotherJameda",
  kununu: "platforms.addAnotherKununu",
};

/** Honest fallback names when the backend hasn't supplied a real profile name. */
const PLATFORM_FALLBACK_NAME = {
  google: "Google Business",
  jameda: "Jameda",
  kununu: "Kununu",
};

function applyDefaultConnectionDetails(list) {
  return list.map((c) => {
    // Use the real profile/location name from the backend; otherwise fall
    // back to the platform's own name rather than inventing a city/company.
    const displayName =
      typeof c.displayName === "string" && c.displayName.trim()
        ? c.displayName.trim()
        : PLATFORM_FALLBACK_NAME[c.platformId] || c.platformId;

    // Only keep a rating that actually came from the backend — never fabricate.
    let avgRating;
    if (typeof c.avgRating === "number" && Number.isFinite(c.avgRating)) {
      avgRating = Math.min(5, Math.max(1, Math.round(c.avgRating * 10) / 10));
    }

    return { ...c, displayName, avgRating };
  });
}

function formatAvgRatingDe(value, locale = "de") {
  return formatAvgRating(value, locale);
}

function loadConnections(companyId) {
  const key = connectionsStorageKey(companyId);
  if (!key) return [];
  const raw = localStorage.getItem(key);
  let list = normalizeStoredConnections(raw);
  if (raw && list.length > 0) {
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        list = applyDefaultConnectionDetails(list);
        saveConnectionsList(companyId, list);
        return list;
      }
    } catch {
      /* ignore */
    }
  }
  const enriched = applyDefaultConnectionDetails(list);
  if (raw && list.length > 0) {
    const dirty = enriched.some((e) => {
      const o = list.find((x) => x.connectionKey === e.connectionKey);
      return (
        !o ||
        o.displayName !== e.displayName ||
        o.avgRating !== e.avgRating
      );
    });
    if (dirty) saveConnectionsList(companyId, enriched);
  }
  return enriched;
}

/**
 * Blocking sync UI only for OAuth return or when integration exists but the local
 * Google connection cache is still empty (first connect). Tab revisits with
 * cached connections render immediately.
 */
function readNeedsBlockingGoogleSync(companyId) {
  if (readGoogleOAuthReturnFromUrl()) return true;
  if (!readHasStoredGoogleIntegration(companyId)) return false;
  const cached = loadConnections(companyId);
  return !cached.some((c) => c.platformId === "google");
}

function saveConnectionsList(companyId, list) {
  const key = connectionsStorageKey(companyId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

function connectionOrdinal(connection, all) {
  const same = all
    .filter((c) => c.platformId === connection.platformId)
    .sort((a, b) => a.connectedAt - b.connectedAt);
  const idx = same.findIndex(
    (c) => c.connectionKey === connection.connectionKey
  );
  return { index: idx + 1, total: same.length };
}

const LogoForId = ({ id }) => {
  if (id === "google") return <GoogleBusinessLogo />;
  if (id === "jameda") return <JamedaLogo />;
  if (id === "kununu") return <KununuLogo />;
  return null;
};

const PlusSmallIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 5v14M5 12h14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const IconMoreVertical = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="text-[#64748b]"
    aria-hidden
  >
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

const StarRatingBadge = ({ value, locale }) => (
  <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-[#fffbeb] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-amber-900 ring-1 ring-amber-200/90">
    <svg
      className="h-3.5 w-3.5 shrink-0 text-amber-500"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
     {formatAvgRatingDe(value, locale)}
  </span>
);

const ClientPlatformsPage = () => {
  const { t, locale } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = getCurrentUser()?.company?.id ?? null;

  const [connections, setConnections] = useState(() =>
    loadConnections(getCurrentUser()?.company?.id ?? null)
  );

  const companyIdEffectRanRef = useRef(false);
  useEffect(() => {
    if (!companyIdEffectRanRef.current) {
      companyIdEffectRanRef.current = true;
      return;
    }
    setConnections(loadConnections(companyId));
  }, [companyId]);

  // Per-platform plan limits (server-side truth): Basic/Premium = 1 profile
  // per platform. Drives the "add another" gate and over-limit banners.
  const [platformLimits, setPlatformLimits] = useState(null);
  const [limitDialogPlatform, setLimitDialogPlatform] = useState(null);

  // Hydrate from the backend so team members — and this user on a new
  // device — see everything the account has connected.
  useEffect(() => {
    let active = true;
    hydrateConnectionsSnapshot(companyId).then((snap) => {
      if (!active || snap === null) return;
      setPlatformLimits(snap.platformLimits);
      if (snap.list.length === 0) return;
      setConnections(loadConnections(getCurrentUser()?.company?.id ?? companyId));
    });
    return () => {
      active = false;
    };
  }, [companyId]);

  const refreshPlatformLimits = useCallback(async () => {
    const snap = await hydrateConnectionsSnapshot(
      getCurrentUser()?.company?.id ?? companyId
    );
    if (snap) setPlatformLimits(snap.platformLimits);
    return snap;
  }, [companyId]);

  const limitFor = useCallback(
    (platformId) => platformLimits?.[platformId] ?? null,
    [platformLimits]
  );
  const isAtLimit = useCallback(
    (platformId) => {
      const s = limitFor(platformId);
      return Boolean(s && s.limit != null && s.atLimit);
    },
    [limitFor]
  );
  const [googleSyncError, setGoogleSyncError] = useState("");
  const [googleBusy, setGoogleBusy] = useState(false);
  const oauthReturnOnLoad = readGoogleOAuthReturnFromUrl();
  const needsBlockingSyncOnLoad = readNeedsBlockingGoogleSync(companyId);
  const [googleIntegrationPending, setGoogleIntegrationPending] = useState(
    () => needsBlockingSyncOnLoad
  );
  const [googleIntegrationStep, setGoogleIntegrationStep] = useState(() => {
    if (!needsBlockingSyncOnLoad) return null;
    if (oauthReturnOnLoad && searchParams.get("code") && searchParams.get("state")) {
      return "oauth_callback";
    }
    return "accounts";
  });
  const [disconnectListLoading, setDisconnectListLoading] = useState(false);
  const [googleLocationMeta, setGoogleLocationMeta] = useState(null);
  const googleSyncInFlightRef = useRef(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [modalAllowedIds, setModalAllowedIds] = useState(null);
  const [openManageId, setOpenManageId] = useState(null);
  const [jamedaOpen, setJamedaOpen] = useState(false);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [locationPickerRefresh, setLocationPickerRefresh] = useState(false);
  const manageWrapRefs = useRef({});

  /**
   * Persist the user's confirmed location selection: only locations the
   * backend reports as connected become visible platform connections.
   */
  const applySelectedGoogleLocations = useCallback((payload) => {
    const cid = getCurrentUser()?.company?.id ?? null;
    const iKey = googleIntegrationStorageKey(cid);
    let integrationId = null;
    try {
      integrationId = iKey ? localStorage.getItem(iKey) : null;
    } catch {
      /* ignore */
    }
    const selectedConnections = (payload?.locations || [])
      .filter((loc) => loc.is_connected)
      .map((loc) => connectionFromBackendLocation(loc, integrationId));
    setConnections((prev) => {
      const nonGoogle = prev.filter((c) => c.platformId !== "google");
      const next = applyDefaultConnectionDetails([
        ...nonGoogle,
        ...selectedConnections,
      ]);
      saveConnectionsList(cid, next);
      return next;
    });
    setLocationPickerOpen(false);
    setGoogleSyncError("");
    refreshPlatformLimits();
  }, [refreshPlatformLimits]);

  const closeLocationPicker = useCallback(() => {
    setLocationPickerOpen(false);
    setConnections((prev) => {
      if (!prev.some((c) => c.platformId === "google")) {
        // OAuth done but nothing chosen yet — tell the user how to finish.
        setGoogleSyncError(t("platforms.locationPicker.notCompleted"));
      }
      return prev;
    });
  }, [t]);

  /* Reconcile Jameda: backend is the source of truth for the connection. */
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const status = await fetchJamedaStatus();
        if (!active) return;
        const cid = getCurrentUser()?.company?.id ?? null;
        setConnections((prev) => {
          const hasLocal = prev.some((c) => c.platformId === "jameda");
          if (status.connected && !hasLocal) {
            const next = applyDefaultConnectionDetails([
              ...prev,
              {
                connectionKey: newConnectionKey(),
                platformId: "jameda",
                connectedAt: Date.now(),
                displayName: status.businessName || "Jameda",
                ...(typeof status.averageRating === "number"
                  ? { avgRating: status.averageRating }
                  : {}),
                ...(status.locationId ? { locationId: status.locationId } : {}),
                remoteId: status.businessId,
              },
            ]);
            saveConnectionsList(cid, next);
            return next;
          }
          if (!status.connected && hasLocal) {
            const next = prev.filter((c) => c.platformId !== "jameda");
            saveConnectionsList(cid, next);
            return next;
          }
          return prev;
        });
      } catch {
        /* status check is best-effort */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (openManageId == null) return;
    const onDocMouseDown = (e) => {
      const wrap = manageWrapRefs.current[openManageId];
      if (wrap && !wrap.contains(e.target)) {
        setOpenManageId(null);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [openManageId]);

  const hasAnyConnection = connections.length > 0;

  useLayoutEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const googleReturn = readGoogleOAuthReturnFromUrl();

    if (code && state) {
      setGoogleIntegrationPending(true);
      setGoogleIntegrationStep("oauth_callback");
      setGoogleLocationMeta(null);
      return;
    }

    if (googleReturn) {
      setGoogleIntegrationPending(true);
      setGoogleIntegrationStep((current) => current || "accounts");
      setGoogleLocationMeta(null);
      return;
    }

    if (readNeedsBlockingGoogleSync(companyId)) {
      setGoogleIntegrationPending(true);
      setGoogleIntegrationStep((current) => current || "accounts");
      setGoogleLocationMeta(null);
    }
  }, [searchParams, companyId]);

  useEffect(() => {
    const sessionCompanyId = getCurrentUser()?.company?.id ?? null;
    const integrationKey = googleIntegrationStorageKey(sessionCompanyId);

    /**
     * Locations are imported to the backend, but never auto-connected:
     * the picker lets the user choose within their plan's limit. Only
     * already-confirmed (is_connected) locations are applied silently.
     */
    const applyOrPromptSelection = async () => {
      const payload = await fetchAvailableGoogleLocations();
      const available = payload?.locations || [];
      if (available.length === 0) {
        setGoogleSyncError(t("platforms.errorNoAccounts"));
        return;
      }
      if (payload.over_limit) {
        // More locations connected than the plan allows (legacy data or a
        // trial that converted): reflect the real state, then make the user
        // choose which one to keep — never pick for them.
        applySelectedGoogleLocations(payload);
        setLocationPickerRefresh(false);
        setLocationPickerOpen(true);
        return;
      }
      if ((payload.connected_count || 0) > 0) {
        applySelectedGoogleLocations(payload);
        return;
      }
      setLocationPickerRefresh(false);
      setLocationPickerOpen(true);
    };

    const syncGoogleConnections = async (integrationId) => {
      if (integrationKey && integrationId) {
        try {
          localStorage.setItem(integrationKey, String(integrationId));
        } catch {
          /* ignore */
        }
      }
      setGoogleSyncError("");
      return resolveConnectionsFromIntegration({
        integrationId,
        companyId: sessionCompanyId,
        onPhase: (phase, meta) => {
          if (phase === "locations") {
            setGoogleLocationMeta(meta || null);
            setGoogleIntegrationStep("locations");
          } else if (phase === "accounts") {
            setGoogleLocationMeta(null);
            setGoogleIntegrationStep("accounts");
          }
        },
      });
    };

    const finishSyncUi = () => {
      setGoogleBusy(false);
      setGoogleIntegrationPending(false);
      setGoogleIntegrationStep(null);
      setGoogleLocationMeta(null);
    };

    const resolveSavedIntegrationId = () => {
      let savedIntegrationId =
        integrationKey && localStorage.getItem(integrationKey);
      if (!savedIntegrationId && integrationKey && sessionCompanyId) {
        const connKey = connectionsStorageKey(sessionCompanyId);
        if (connKey) {
          try {
            const raw = localStorage.getItem(connKey);
            const parsed = raw ? JSON.parse(raw) : [];
            const row = Array.isArray(parsed)
              ? parsed.find(
                  (c) =>
                    c &&
                    c.platformId === "google" &&
                    c.integrationId != null
                )
              : null;
            if (row) {
              const iid = Number(row.integrationId);
              if (Number.isFinite(iid) && iid > 0) {
                savedIntegrationId = String(iid);
                localStorage.setItem(integrationKey, savedIntegrationId);
              }
            }
          } catch {
            /* ignore */
          }
        }
      }
      return savedIntegrationId;
    };

    const resolveIntegrationIdFromBackend = async () => {
      try {
        const status = await fetchGoogleIntegrationStatus();
        const remoteId = status?.integration_id;
        if (status?.connected && remoteId != null && Number(remoteId) > 0) {
          const asString = String(remoteId);
          if (integrationKey) {
            localStorage.setItem(integrationKey, asString);
          }
          return asString;
        }
      } catch {
        /* ignore */
      }
      return null;
    };

    const runSavedIntegrationSync = async (savedIntegrationId) => {
      setGoogleIntegrationPending(true);
      setGoogleIntegrationStep("accounts");
      setGoogleLocationMeta(null);
      setGoogleBusy(true);
      try {
        await syncGoogleConnections(savedIntegrationId);
        await applyOrPromptSelection();
      } catch (err) {
        setGoogleSyncError(
          err?.message ||
            err?.data?.detail ||
            t("platforms.errorLoadFailed")
        );
      } finally {
        finishSyncUi();
      }
    };

    const run = async () => {
      if (googleSyncInFlightRef.current) return;
      googleSyncInFlightRef.current = true;

      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const hasOAuthParams = Boolean(code && state);
      const googleReturnHint = readGoogleOAuthReturnFromUrl();

      let integrationId = resolveSavedIntegrationId();

      try {
        const hasCachedGoogle = loadConnections(sessionCompanyId).some(
          (c) => c.platformId === "google"
        );

        if (!hasOAuthParams && hasCachedGoogle) {
          if (googleReturnHint) {
            clearOAuthQueryParams(setSearchParams, searchParams);
          }
          return;
        }

        const willSync = hasOAuthParams || Boolean(integrationId);

        if (!willSync) {
          if (googleReturnHint) {
            clearOAuthQueryParams(setSearchParams, searchParams);
            setGoogleSyncError(t("platforms.errorOAuthIncomplete"));
            finishSyncUi();
          }
          return;
        }

        // JWT + profile must match before any Google sync (prevents stale company_id in localStorage).
        const verifiedCompanyId = await requireClientCompanyContext();
        const verifiedIntegrationKey = googleIntegrationStorageKey(verifiedCompanyId);

        if (!hasOAuthParams && verifiedIntegrationKey) {
          const savedForCompany = localStorage.getItem(verifiedIntegrationKey);
          if (savedForCompany) {
            integrationId = savedForCompany;
          }
        }

        if (!hasOAuthParams && !integrationId && googleReturnHint) {
          integrationId = await resolveIntegrationIdFromBackend();
        }

        if (hasOAuthParams) {
          setGoogleIntegrationPending(true);
          setGoogleIntegrationStep("oauth_callback");
          setGoogleLocationMeta(null);
          setGoogleBusy(true);

          const callbackData = await completeGoogleOAuth({ code, state });
          const callbackIntegrationId = callbackData?.integration_id;
          if (callbackIntegrationId != null && Number(callbackIntegrationId) > 0) {
            integrationId = String(callbackIntegrationId);
          }

          if (!integrationId) {
            setGoogleSyncError(t("platforms.errorSaveFailed"));
            clearOAuthQueryParams(setSearchParams, searchParams);
            finishSyncUi();
            return;
          }

          await syncGoogleConnections(integrationId);
          await applyOrPromptSelection();

          clearOAuthQueryParams(setSearchParams, searchParams);
          finishSyncUi();
          return;
        }

        if (integrationId) {
          clearOAuthQueryParams(setSearchParams, searchParams);
          await runSavedIntegrationSync(integrationId);
        }
      } catch (err) {
        clearOAuthQueryParams(setSearchParams, searchParams);
        setGoogleSyncError(
          err?.data?.detail ||
            err?.message ||
            t("platforms.errorOAuthFailed")
        );
        finishSyncUi();
      } finally {
        if (hasOAuthParams && state) {
          clearOAuthSessionCheckpoint(state);
        }
        googleSyncInFlightRef.current = false;
      }
    };

    run();
  }, [searchParams, setSearchParams, companyId, t, applySelectedGoogleLocations]);

  const hasGoogleType = useMemo(
    () => connections.some((c) => c.platformId === "google"),
    [connections]
  );
  const hasKununuType = useMemo(
    () => connections.some((c) => c.platformId === "kununu"),
    [connections]
  );

  /** Nur Plattformtypen ohne mindestens eine Verbindung (weitere nur über ⋮). */
  const platformIdsNotYetConnected = useMemo(
    () =>
      PLATFORM_IDS.filter(
        (id) => !connections.some((c) => c.platformId === id)
      ),
    [connections]
  );

  const sortedConnections = useMemo(() => {
    return [...connections].sort((a, b) => {
      const ai = PLATFORM_IDS.indexOf(a.platformId);
      const bi = PLATFORM_IDS.indexOf(b.platformId);
      if (ai !== bi) return ai - bi;
      return a.connectedAt - b.connectedAt;
    });
  }, [connections]);

  const openModal = useCallback((allowedIds) => {
    setModalAllowedIds(allowedIds);
    setConnectOpen(true);
  }, []);

  const handleConnect = useCallback(async (platformId) => {
    if (!platformId || !PLATFORM_IDS.includes(platformId)) return;
    const cid = getCurrentUser()?.company?.id ?? null;

    if (platformId === "jameda") {
      setJamedaOpen(true);
      return;
    }

    if (platformId === "google") {
      setGoogleBusy(true);
      setGoogleSyncError("");
      saveOAuthSessionCheckpoint();
      try {
        const result = await connectGoogleBusiness();
        if (result.type === "redirect" && result.url) {
          setGoogleIntegrationPending(true);
          setGoogleIntegrationStep("oauth_callback");
          setGoogleLocationMeta(null);
          let oauthState = null;
          try {
            oauthState = new URL(result.url).searchParams.get("state");
          } catch {
            /* ignore */
          }
          saveOAuthSessionCheckpoint(oauthState);
          window.location.assign(result.url);
          return;
        }
        if (result.type === "connected" && result.connection) {
          setConnections((prev) => {
            const draft = [...prev, result.connection];
            const enriched = applyDefaultConnectionDetails(draft);
            saveConnectionsList(cid, enriched);
            return enriched;
          });
        }
      } catch (err) {
        setGoogleSyncError(
          err?.data?.detail || err?.message || t("platforms.errorBackend")
        );
      } finally {
        setGoogleBusy(false);
      }
      return;
    }

    setConnections((prev) => {
      const draft = [
        ...prev,
        {
          connectionKey: newConnectionKey(),
          platformId,
          connectedAt: Date.now(),
        },
      ];
      const enriched = applyDefaultConnectionDetails(draft);
      saveConnectionsList(cid, enriched);
      return enriched;
    });
  }, []);

  const handleJamedaConnected = useCallback((result) => {
    const cid = getCurrentUser()?.company?.id ?? null;
    setConnections((prev) => {
      const withoutJameda = prev.filter((c) => c.platformId !== "jameda");
      const next = applyDefaultConnectionDetails([
        ...withoutJameda,
        {
          connectionKey: newConnectionKey(),
          platformId: "jameda",
          connectedAt: Date.now(),
          displayName: result.businessName || "Jameda",
          ...(typeof result.averageRating === "number"
            ? { avgRating: result.averageRating }
            : {}),
          ...(result.locationId ? { locationId: result.locationId } : {}),
          remoteId: result.businessId,
        },
      ]);
      saveConnectionsList(cid, next);
      return next;
    });
    setJamedaOpen(false);
    refreshPlatformLimits();
  }, [refreshPlatformLimits]);

  const handleDisconnect = useCallback(async (connectionKey) => {
    if (!connectionKey) return;
    const target = connections.find((c) => c.connectionKey === connectionKey);
    if (!target) return;
    const cid = getCurrentUser()?.company?.id ?? null;
    const iKey = googleIntegrationStorageKey(cid);

    if (target.platformId === "jameda") {
      try {
        await disconnectJameda();
      } catch {
        /* backend row may already be gone; still remove locally */
      }
      setConnections((prev) => {
        const next = prev.filter((c) => c.connectionKey !== connectionKey);
        saveConnectionsList(cid, next);
        return next;
      });
      setOpenManageId(null);
      refreshPlatformLimits();
      return;
    }

    if (target.platformId === "google") {
      setGoogleSyncError("");
      setDisconnectListLoading(true);
      setGoogleBusy(true);
      try {
        await disconnectGoogleBusiness(target);
        flushSync(() => {
          setConnections((prev) => {
            const next = prev.filter((c) => c.connectionKey !== connectionKey);
            saveConnectionsList(cid, next);
            if (iKey && !next.some((c) => c.platformId === "google")) {
              try {
                localStorage.removeItem(iKey);
              } catch {
                /* ignore */
              }
            }
            return next;
          });
        });
      } finally {
        setDisconnectListLoading(false);
        setGoogleBusy(false);
      }
    } else {
      setConnections((prev) => {
        const next = prev.filter((c) => c.connectionKey !== connectionKey);
        saveConnectionsList(cid, next);
        return next;
      });
    }
    setOpenManageId(null);
    refreshPlatformLimits();
  }, [connections, refreshPlatformLimits]);

  const openAddSamePlatform = useCallback(
    (platformId) => {
      setOpenManageId(null);
      // Jameda's entry re-enters credentials for the SAME profile (allowed
      // on every plan); a different profile is refused by the server and
      // the connect modal shows the plan-limit notice.
      if (platformId !== "jameda" && isAtLimit(platformId)) {
        setLimitDialogPlatform(platformId);
        return;
      }
      openModal([platformId]);
    },
    [openModal, isAtLimit]
  );

  const overLimitPlatforms = useMemo(
    () => Object.entries(platformLimits || {}).filter(([, s]) => s && s.overLimit),
    [platformLimits]
  );

  /** Veil over the connections list: initial/resume sync, OAuth, or Google disconnect. */
  const listVeilActive =
    googleIntegrationPending || disconnectListLoading;

  const syncStep = disconnectListLoading ? "disconnect" : googleIntegrationStep;
  const syncCopy = googleStepCopy(
    t,
    syncStep,
    disconnectListLoading ? null : googleLocationMeta
  );

  // Honest completeness: share of the currently available real platforms
  // (Google + Jameda) that this company has connected.
  const AVAILABLE_PLATFORM_TYPES = ["google", "jameda"];
  const connectedTypeCount = AVAILABLE_PLATFORM_TYPES.filter((type) =>
    connections.some((c) => c.platformId === type)
  ).length;
  const healthPercent = Math.round(
    (connectedTypeCount / AVAILABLE_PLATFORM_TYPES.length) * 100
  );

  /** Same full page as “already connected”, including initial OAuth (veil only on list area). */
  const showPlatformsDashboard =
    hasAnyConnection ||
    googleIntegrationPending ||
    disconnectListLoading ||
    readNeedsBlockingGoogleSync(companyId);

  return (
    <>
      {!showPlatformsDashboard ? (
        <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6 py-16">
          <div
            className="mb-10 flex h-[220px] w-[220px] shrink-0 items-center justify-center rounded-full bg-white shadow-[0_10px_40px_rgba(15,23,42,0.08),0_2px_8px_rgba(15,23,42,0.04)]"
            aria-hidden
          >
            <EcosystemIllustration />
          </div>

          <h1 className="max-w-xl text-center text-[26px] font-bold leading-tight tracking-tight text-[#0f172a] md:text-[32px]">
            {t("platforms.emptyTitle")}
          </h1>

          <p className="mx-auto mt-5 max-w-lg text-center text-[15px] leading-[1.65] text-[#6b7280] md:text-[16px]">
            {t("platforms.emptyBody")}
          </p>

          {googleSyncError && (
            <p className="mx-auto mt-6 max-w-lg rounded-[10px] border border-amber-300 bg-amber-50 px-4 py-3 text-center text-[13px] text-amber-900">
              {googleSyncError}
            </p>
          )}

          <Button
            type="button"
            variant="teal"
            icon={<LinkChainIcon />}
            className="mt-10 max-w-full flex-row-reverse px-10"
            onClick={() => openModal(PLATFORM_IDS)}
          >
            {t("platforms.connectPlatform")}
          </Button>

          <div className="mt-20 flex w-full max-w-lg items-center gap-5 px-2">
            <div className="h-px min-w-0 flex-1 bg-[#e5e7eb]" />
            <span className="shrink-0 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9ca3af]">
              {t("platforms.architectIntelligence")}
            </span>
            <div className="h-px min-w-0 flex-1 bg-[#e5e7eb]" />
          </div>
        </div>
      ) : (
        <div className="min-h-screen bg-[#f4f6f7] px-4 py-8 md:px-8 md:py-10">
          <div className="mx-auto max-w-6xl">
            <header className="mb-8 md:mb-10">
              <h1 className="text-[26px] font-bold tracking-tight text-[#0f172a] md:text-[30px]">
                {t("platforms.ecosystemTitle")}
              </h1>
              <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-[#6b7280]">
                {t("platforms.ecosystemSubtitle")}
              </p>
              {(googleSyncError ||
                (googleBusy &&
                  !googleIntegrationPending &&
                  !listVeilActive)) && (
                <div className="mt-4">
                  {googleSyncError && (
                    <p className="rounded-[10px] border border-amber-300 bg-amber-50 px-4 py-2 text-[13px] text-amber-800">
                      {googleSyncError}
                    </p>
                  )}
                  {googleBusy && !googleIntegrationPending && !listVeilActive && (
                    <p className="mt-2 text-[13px] text-[#64748b]">
                      {t("platforms.syncing")}
                    </p>
                  )}
                </div>
              )}
            </header>

            <div className="flex flex-col gap-8 lg:flex-row lg:gap-10">
              <div className="min-w-0 flex-1 space-y-10">
                <section>
                  <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="text-lg font-bold text-[#0f172a]">
                      {t("platforms.connectedPlatforms")}
                    </h2>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#9ca3af]">
                      {connections.length}{" "}
                      {connections.length === 1
                        ? t("platforms.activeConnection")
                        : t("platforms.activeConnections")}
                    </span>
                  </div>
                  {overLimitPlatforms.length > 0 && !listVeilActive ? (
                    <div className="mb-4 space-y-3">
                      {overLimitPlatforms.map(([id, s]) => (
                        <PlanLimitBanner
                          key={id}
                          platformId={id}
                          connectedCount={s.connectedCount}
                          limit={s.limit}
                          onChoose={
                            id === "google"
                              ? () => {
                                  setLocationPickerRefresh(false);
                                  setLocationPickerOpen(true);
                                }
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  ) : null}
                  <div className="w-full">
                    {listVeilActive ? (
                      <div className="space-y-4">
                        <PlatformsSyncLoadingPanel
                          title={syncCopy.title}
                          detail={syncCopy.detail}
                          step={syncStep}
                          meta={disconnectListLoading ? null : googleLocationMeta}
                        />
                        {connections.length === 0 ? (
                          <>
                            <PlatformsConnectionSkeleton />
                            <PlatformsConnectionSkeleton />
                          </>
                        ) : null}
                      </div>
                    ) : (
                      <ul className="space-y-3">
                    {sortedConnections.map((conn) => {
                      const meta = getPlatformById(conn.platformId, t);
                      const { index, total } = connectionOrdinal(
                        conn,
                        connections
                      );
                      const ts = conn.connectedAt;
                      const ordinalLine =
                        total > 1
                          ? conn.platformId === "google"
                            ? t("platforms.locationOrdinal", { index, total })
                            : t("platforms.profileOrdinal", { index, total })
                          : null;
                      return (
                        <li
                          key={conn.connectionKey}
                          className="relative rounded-[14px] border border-[#e8ecf0] bg-white py-4 pl-4 pr-11 shadow-[0_1px_3px_rgba(15,23,42,0.06)] sm:pr-12"
                        >
                          <div className="flex min-w-0 items-start gap-4">
                            <LogoForId id={conn.platformId} />
                            <div className="min-w-0">
                              <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 pr-1 text-[15px] font-bold leading-snug text-[#0f172a]">
                                <span className="min-w-0">
                                  {meta?.title ?? conn.platformId}
                                  <span className="ml-1 font-medium text-[#94a3b8]">
                                    ({conn.displayName})
                                  </span>
                                </span>
                                {typeof conn.avgRating === "number" ? (
                                  <StarRatingBadge value={conn.avgRating} locale={locale} />
                                ) : null}
                              </p>
                              {ordinalLine && (
                                <p className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#94a3b8]">
                                  {ordinalLine}
                                </p>
                              )}
                              <p className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-[#64748b]">
                                <span className="inline-flex items-center gap-1.5">
                                  <span
                                    className="h-2 w-2 shrink-0 rounded-full bg-emerald-500"
                                    aria-hidden
                                  />
                                  <span className="font-medium text-[#0f172a]">
                                    {t("platforms.active")}
                                  </span>
                                </span>
                                <span className="text-[#cbd5e1]">·</span>
                                <span>
                                  {t("platforms.lastSynced", {
                                    time: formatRelativeTime(t, ts),
                                  })}
                                </span>
                                <span className="text-[#cbd5e1]">·</span>
                                <span>{t("platforms.apiOk")}</span>
                              </p>
                            </div>
                          </div>
                          <div
                            className="absolute right-1 top-1 sm:right-2 sm:top-2"
                            ref={(el) => {
                              manageWrapRefs.current[conn.connectionKey] = el;
                            }}
                          >
                            <button
                              type="button"
                              className="flex h-8 w-8 items-center justify-center rounded-lg text-[#64748b] outline-none transition hover:bg-[#f1f5f9] focus-visible:ring-2 focus-visible:ring-[#006666] focus-visible:ring-offset-2"
                              aria-expanded={
                                openManageId === conn.connectionKey
                              }
                              aria-haspopup="menu"
                              aria-label={t("platforms.manage")}
                              title={t("platforms.manage")}
                              onClick={() =>
                                setOpenManageId((cur) =>
                                  cur === conn.connectionKey
                                    ? null
                                    : conn.connectionKey
                                )
                              }
                            >
                              <IconMoreVertical />
                            </button>
                            {openManageId === conn.connectionKey && (
                              <div
                                className="absolute right-0 top-[calc(100%+2px)] z-20 w-max min-w-[200px] max-w-[min(260px,calc(100vw-2rem))] overflow-hidden rounded-lg border border-[#e5e7eb] bg-white py-0.5 shadow-[0_8px_24px_rgba(15,23,42,0.1)]"
                                role="menu"
                              >
                                {conn.platformId === "google" && (
                                  <>
                                    <button
                                      type="button"
                                      role="menuitem"
                                      className="w-full px-3 py-2 text-left text-[12px] font-medium text-[#0f172a] transition hover:bg-[#f8fafc]"
                                      onClick={() => {
                                        setOpenManageId(null);
                                        setLocationPickerRefresh(true);
                                        setLocationPickerOpen(true);
                                      }}
                                    >
                                      {t("platforms.locationPicker.manageMenu")}
                                    </button>
                                    <div
                                      className="my-0.5 h-px bg-[#f1f5f9]"
                                      aria-hidden
                                    />
                                  </>
                                )}
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="w-full px-3 py-2 text-left text-[12px] font-medium text-[#0f172a] transition hover:bg-[#f8fafc]"
                                  onClick={() =>
                                    openAddSamePlatform(conn.platformId)
                                  }
                                >
                                  <span className="flex items-center justify-between gap-3">
                                    {t(ADD_ANOTHER_LABEL_KEY[conn.platformId])}
                                    {conn.platformId !== "jameda" &&
                                    isAtLimit(conn.platformId) ? (
                                      <span className="rounded-full bg-[#f1f5f9] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#64748b]">
                                        {t("platforms.limit.badge")}
                                      </span>
                                    ) : null}
                                  </span>
                                </button>
                                <div
                                  className="my-0.5 h-px bg-[#f1f5f9]"
                                  aria-hidden
                                />
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="w-full px-3 py-2 text-left text-[12px] font-medium text-red-600 transition hover:bg-red-50"
                                  onClick={() =>
                                    handleDisconnect(conn.connectionKey)
                                  }
                                >
                                  {t("platforms.disconnect")}
                                </button>
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                      </ul>
                    )}
                  </div>
                </section>

                {platformIdsNotYetConnected.length > 0 &&
                  !listVeilActive && (
                  <section>
                    <div className="mb-4">
                      <h2 className="text-lg font-bold text-[#0f172a]">
                        {t("platforms.addPlatform")}
                      </h2>
                      <p className="mt-1 text-[14px] text-[#6b7280]">
                        {t("platforms.addPlatformHint")}
                      </p>
                    </div>
                    <ul className="grid gap-3 sm:grid-cols-1">
                      {platformIdsNotYetConnected.map((id) => {
                        const meta = getPlatformById(id, t);
                        return (
                          <li
                            key={id}
                            className="flex items-center justify-between gap-4 rounded-[14px] border border-[#e8ecf0] bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.06)]"
                          >
                            <div className="flex min-w-0 items-center gap-4">
                              <LogoForId id={id} />
                              <span className="font-semibold text-[#0f172a]">
                                {meta?.title ?? id}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => openModal([id])}
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e5e7eb] bg-[#f8fafc] text-[#475569] transition hover:border-[#006666] hover:bg-[#006666] hover:text-white"
                              aria-label={t("platforms.connectAria", {
                                name: meta?.title ?? id,
                              })}
                            >
                              <PlusSmallIcon />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}
              </div>

              <aside className="w-full shrink-0 space-y-6 lg:w-[300px]">
                {listVeilActive && connections.length === 0 ? (
                  <PlatformsSidebarSkeleton />
                ) : (
                  <>
                <div className="overflow-hidden rounded-[16px] bg-gradient-to-br from-[#0f172a] to-[#1e293b] p-6 text-white shadow-lg">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/70">
                    {t("platforms.ecosystemStatus")}
                  </p>
                  <p className="mt-3 text-4xl font-bold tabular-nums">
                    {healthPercent}%
                  </p>
                  <p className="mt-3 text-[13px] leading-relaxed text-white/85">
                    {healthPercent >= 100
                      ? t("platforms.healthOptimal")
                      : t("platforms.healthPartial")}
                  </p>
                  <div className="mt-5 h-2 w-full overflow-hidden rounded-full bg-white/20">
                    <div
                      className="h-full rounded-full bg-emerald-400 transition-[width]"
                      style={{ width: `${healthPercent}%` }}
                    />
                  </div>
                </div>

                <div className="rounded-[16px] border border-[#e8ecf0] bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9ca3af]">
                    {t("platforms.upcomingSyncs")}
                  </p>
                  <ul className="mt-4 space-y-4">
                    {sortedConnections.map((conn) => {
                      const meta = getPlatformById(conn.platformId, t);
                      const ratingPart =
                        typeof conn.avgRating === "number"
                          ? `${formatAvgRatingDe(conn.avgRating, locale)} · `
                          : "";
                      const subTail = ` ${ratingPart}${t("platforms.metadataReconcile")}`;
                      return (
                        <li
                          key={conn.connectionKey}
                          className="flex gap-3 border-b border-[#f1f5f9] pb-4 last:border-0 last:pb-0"
                        >
                          <span className="w-16 shrink-0 text-[11px] font-semibold uppercase tracking-[0.04em] text-[#94a3b8]">
                            {t("platforms.syncCadence")}
                          </span>
                          <div className="min-w-0">
                            <p className="text-[13px] font-bold leading-snug text-[#0f172a]">
                              {meta?.title ?? conn.platformId}
                              <span className="ml-1 font-medium text-[#94a3b8]">
                                ({conn.displayName})
                              </span>
                            </p>
                            <p className="mt-0.5 text-[12px] leading-relaxed text-[#64748b]">
                              {subTail}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <PlanLimitSidebarCard platformLimits={platformLimits} />
                  </>
                )}
              </aside>
            </div>
          </div>
        </div>
      )}

      <ConnectPlatformModal
        open={connectOpen}
        allowedPlatformIds={modalAllowedIds}
        onClose={() => setConnectOpen(false)}
        onConnect={handleConnect}
      />

      <JamedaConnectModal
        open={jamedaOpen}
        onClose={() => setJamedaOpen(false)}
        onConnected={handleJamedaConnected}
      />

      <GoogleLocationPickerModal
        open={locationPickerOpen}
        onClose={closeLocationPicker}
        onApplied={applySelectedGoogleLocations}
        refreshOnOpen={locationPickerRefresh}
      />

      <PlanLimitDialog
        open={Boolean(limitDialogPlatform)}
        platformId={limitDialogPlatform || "google"}
        limit={limitFor(limitDialogPlatform)?.limit ?? 1}
        connectedCount={limitFor(limitDialogPlatform)?.connectedCount ?? 1}
        onClose={() => setLimitDialogPlatform(null)}
        onManageCurrent={
          limitDialogPlatform === "google"
            ? () => {
                setLocationPickerRefresh(true);
                setLocationPickerOpen(true);
              }
            : undefined
        }
      />
    </>
  );
};

export default ClientPlatformsPage;
